/**
 * Müşteri (CRM kaydından, salt okunur): ad kısaltması, kanal çipi, uyruk çipi. Uyruk fiyat sorgusunun parametresi ve
 * kaynağı müşteri kaydıdır; kayıtta yoksa alan boş kalır ve "Uyruk eksik" olur (şartname 5). Müşteri seçili değilse
 * kısa bir arama kutusu açılır (mevcut `searchCustomers` ucu; yeni uç yok).
 */
import { useEffect, useId, useState } from 'react';
import { api, type CustomerListItem } from '../api';
import { HIZLI_METIN as M } from '../metin/hizliTeklif';
import { MusteriEkleModal } from './MusteriEkleModal';
import { Ikon } from './Ikon';

export interface MusteriBilgisi {
  readonly id: string;
  readonly ad: string;
  readonly uyruk: string | null;
  readonly whatsapp: boolean;
}

/** "Ayşe Yılmaz" -> "A. Y." (taslakta B. T.). Tam ad yalnız başlık ipucunda. */
export function kisaltma(ad: string): string {
  const p = ad.split(/\s+/).filter(Boolean);
  if (p.length === 0) return '';
  return p.slice(0, 3).map((x) => `${x.charAt(0).toLocaleUpperCase('tr-TR')}.`).join(' ');
}

export function MusteriAlani({ musteri, yukleniyor, hata, sec, degistir, devreDisi, ekleyebilir = false }: { ekleyebilir?: boolean; musteri: MusteriBilgisi | null; yukleniyor: boolean; hata: string | null; sec: (id: string) => void; degistir: () => void; devreDisi: boolean }) {
  const kimlik = useId();
  const [metin, setMetin] = useState('');
  const [liste, setListe] = useState<readonly CustomerListItem[]>([]);
  const [arandi, setArandi] = useState(false);
  const [aramaHata, setAramaHata] = useState(false);
  const [ekle, setEkle] = useState(false);
  useEffect(() => {
    const q = metin.trim();
    setListe([]); setArandi(false); setAramaHata(false);
    if (musteri !== null || q.length < 2) { setListe([]); setArandi(false); return undefined; }
    const iptal = new AbortController();
    const t = window.setTimeout(() => {
      api.searchCustomers(q).then((v) => { if (!iptal.signal.aborted) { setListe(v.items); setArandi(true); } }).catch(() => { if (!iptal.signal.aborted) { setListe([]); setAramaHata(true); } });
    }, 250);
    return () => { window.clearTimeout(t); iptal.abort(); };
  }, [metin, musteri]);

  if (musteri === null) {
    return (
      <div className="ht-alan" data-testid="ht-musteri-sec">
        <label htmlFor={`${kimlik}-m`}>{M.musteriAra}</label>
        <div className="ht-girdi">
          <Ikon ad="kisi" />
          <input id={`${kimlik}-m`} type="search" autoComplete="off" placeholder={M.musteriAraIpucu} value={metin} maxLength={80} disabled={yukleniyor || devreDisi} data-testid="ht-musteri-ara" onChange={(e) => { setMetin(e.target.value); setArandi(false); setListe([]); setAramaHata(false); }} />
        </div>
        {yukleniyor ? <span className="ht-ipucu">{M.yukleniyor}</span> : <span className="ht-ipucu">{hata ?? M.musteriYok}</span>}
        {aramaHata ? <span role="alert" className="ht-hata-yazi">{M.musteriAramaHata}</span> : null}
        {arandi && liste.length === 0 ? <ul className="ht-oneri ht-oneri-satir" aria-label={M.musteriSec}><li>
          <span className="ht-ipucu">{M.musteriBulunamadi}</span>
          {ekleyebilir ? <button type="button" className="ht-oneri-dugme" disabled={devreDisi || yukleniyor} data-testid="ht-musteri-ekle" onClick={() => setEkle(true)}>{M.musteriEkle(metin.trim())}</button> : null}
        </li></ul> : null}
        {ekle && ekleyebilir && !devreDisi ? <MusteriEkleModal ad={metin.trim()} kapat={() => setEkle(false)} sec={(id) => { setEkle(false); sec(id); }} /> : null}
        {liste.length > 0 ? (
          <ul className="ht-oneri ht-oneri-satir" aria-label={M.musteriSec}>
            {liste.map((c) => <li key={c.id}><button type="button" className="ht-oneri-dugme" onClick={() => sec(c.id)} data-testid={`ht-musteri-${c.id}`}>{c.displayName}</button></li>)}
          </ul>
        ) : null}
      </div>
    );
  }
  return (
    <div className="ht-alan">
      <span className="ht-etiket" id={`${kimlik}-b`}>{M.musteri}</span>
      <div className="ht-girdi ht-girdi-salt" role="group" aria-labelledby={`${kimlik}-b`} data-testid="ht-musteri">
        <Ikon ad="kisi" />
        <b title={musteri.ad} data-testid="ht-musteri-ad">{kisaltma(musteri.ad)}</b>
        {musteri.whatsapp ? <span className="ht-chip ht-chip-ok">{M.kanalWhatsapp}</span> : null}
        {musteri.uyruk !== null ? <span className="ht-chip" aria-label={M.uyrukChip(musteri.uyruk)} data-testid="ht-uyruk">{musteri.uyruk}</span> : <span className="ht-chip ht-chip-uyari" data-testid="ht-uyruk-eksik">{M.uyrukEksik}</span>}
      </div>
      <button type="button" className="ht-baglanti ht-musteri-degistir" disabled={devreDisi} onClick={degistir}>{M.musteriDegistir}</button>
      {musteri.uyruk === null ? (
        <span className="ht-hata-yazi" data-testid="ht-uyruk-eksik-acik">{M.uyrukEksikAcik}</span>
      ) : null}
    </div>
  );
}
